import {
  BridgeError,
  type CameraInfo,
  HttpBridge,
  type PropertyDescriptor,
  type PropertyValue,
} from '@kinoq/camera-protocol';
import { type FormEvent, useEffect, useState } from 'react';
import { setBridgeProperty } from '@/camera/bridgeSettings';
import { type CameraConfig, useCameraConfig } from '@/camera/cameraConfig';
import { bridgeUrl, shellApi } from '@/camera/desktopDiscovery';
import { lensSubtitle, parseSize } from '@/camera/lensSubtitle';
import { useBridges } from '@/camera/useBridges';
import { useLocalCamera } from '@/camera/useLocalCamera';
import { ZoomSlider } from './camera/ZoomSlider';
import { Alert, Button, Drawer, Muted, TextInput } from './ui';

const DEFAULT_PORT = '4310';
const selectClass =
  'min-h-11 w-full rounded-xl border border-divider bg-surface-primary px-3 py-2 text-base text-content-primary';

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Which cameras this device shows on the camera screen, and the bridges' lens and settings. */
export function CamerasPage({ onClose }: { onClose: () => void }) {
  const [config, update] = useCameraConfig();
  const bridges = useBridges({ poll: true });
  const shown = bridges.filter((b) => !b.hidden);
  const hidden = bridges.filter((b) => b.hidden);
  const inShell = shellApi() !== undefined;

  return (
    <Drawer title="Cameras" onClose={onClose}>
      <ul className="space-y-3">
        <LocalRow config={config} update={update} />
        {shown.map((b) => (
          <BridgeRow key={b.id} bridge={b} update={update} />
        ))}
      </ul>
      {inShell ? (
        <>
          <AddByAddress update={update} />
          {hidden.length > 0 && (
            <details>
              <summary className="min-h-11 cursor-pointer py-2 text-sm font-medium text-content-secondary">
                Hidden ({hidden.length})
              </summary>
              <ul className="space-y-2">
                {hidden.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-base text-content-primary">
                      {b.name}
                    </span>
                    <Button
                      variant="secondary"
                      aria-label={`Show ${b.name}`}
                      onClick={() => {
                        update((c) => setHidden(c, b, false));
                        // Hide made the shell forget the address, and discovery will not report it.
                        if (b.manual)
                          void shellApi()
                            ?.bridges?.addBridge({ host: b.host, port: b.port })
                            .catch(() => {});
                      }}
                    >
                      Show
                    </Button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : (
        <Muted>Bridge cameras need the RosaHouse tablet or desktop app.</Muted>
      )}
    </Drawer>
  );
}

type Update = (f: (c: CameraConfig) => CameraConfig) => void;
type Known = ReturnType<typeof useBridges>[number];

const savedOf = (b: Known) => ({ active: false, name: b.name, host: b.host, port: b.port });

function setHidden(c: CameraConfig, b: Known, hide: boolean): CameraConfig {
  return {
    ...c,
    bridges: { ...c.bridges, [b.id]: { ...(c.bridges[b.id] ?? savedOf(b)), hidden: hide } },
  };
}

function LocalRow({ config, update }: { config: CameraConfig; update: Update }) {
  // Plain-http origins have no getUserMedia; the lens list then stays empty.
  const canOpen = config.local.active && !!navigator.mediaDevices?.getUserMedia;
  const { devices, maxSize, zoomRange } = useLocalCamera(
    canOpen,
    config.local.deviceId,
    config.local.zoom,
  );
  const subtitle = lensSubtitle({ ...maxSize });
  // With the camera off nothing is listed, so an id absent from the list proves nothing.
  const missing = canOpen && config.local.deviceId;
  return (
    <li aria-label="This device" className="space-y-2 rounded-xl border border-divider p-3">
      <ShowBox
        label="This device"
        checked={config.local.active}
        onChange={(active) => update((c) => ({ ...c, local: { ...c.local, active } }))}
      />
      <select
        aria-label="Lens"
        className={selectClass}
        value={config.local.deviceId ?? ''}
        onChange={(e) =>
          update((c) => ({
            ...c,
            local: { active: c.local.active, ...(e.target.value && { deviceId: e.target.value }) },
          }))
        }
      >
        <option value="">Rear camera</option>
        {missing && !devices.some((d) => d.deviceId === missing) && (
          <option value={missing} disabled>
            Unavailable camera
          </option>
        )}
        {devices.map((d) => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label}
          </option>
        ))}
      </select>
      {canOpen && subtitle && <Subtitle>{subtitle}</Subtitle>}
      {canOpen && zoomRange && (
        <ZoomSlider
          {...zoomRange}
          value={config.local.zoom ?? 1}
          onCommit={(zoom) => update((c) => ({ ...c, local: { ...c.local, zoom } }))}
        />
      )}
    </li>
  );
}

const Subtitle = ({ children }: { children: string }) => (
  <p className="text-sm text-content-secondary">{children}</p>
);

function ShowBox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 items-center gap-3 text-base font-medium text-content-primary">
      <input
        type="checkbox"
        aria-label={`Show ${label} on camera screen`}
        className="size-5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

function statusOf(b: Known): string {
  switch (b.probe?.kind) {
    case undefined:
      return 'checking…';
    case 'live':
      return `connected · ${b.probe.info.viewers} watching`;
    case 'unknown':
      return 'not found on the network';
    case 'old':
      return 'needs a newer bridge';
    case 'unreachable':
      return 'not answering';
  }
}

interface Controls {
  cameras: CameraInfo[];
  lens: string | null;
  props: PropertyDescriptor[];
}

// Connecting to the bound lens is idempotent and keeps its properties; it opens the session
// the properties are read through.
async function readControls(id: string, lens: string | null): Promise<Controls> {
  const bridge = new HttpBridge(bridgeUrl(id));
  const cameras = await bridge.listCameras();
  if (lens === null) return { cameras, lens, props: [] };
  await bridge.connect(lens);
  return { cameras, lens, props: await bridge.listProperties() };
}

function BridgeRow({ bridge, update }: { bridge: Known; update: Update }) {
  const bound = bridge.probe?.kind === 'live' ? bridge.probe.info.bound : undefined;
  const [controls, setControls] = useState<Controls>();
  const [error, setError] = useState('');

  useEffect(() => {
    if (bound === undefined) return;
    let stale = false;
    readControls(bridge.id, bound).then(
      (c) => !stale && setControls(c),
      (e: unknown) => !stale && setError(message(e)),
    );
    return () => {
      stale = true;
    };
  }, [bridge.id, bound]);

  const run = async (work: () => Promise<void>): Promise<boolean> => {
    setError('');
    try {
      await work();
      return true;
    } catch (e) {
      setError(e instanceof BridgeError ? e.message : message(e));
      return false;
    }
  };

  const chooseLens = (lens: string) =>
    run(async () => setControls(await readControls(bridge.id, lens)));

  const set = (name: string, value: PropertyValue) =>
    run(async () => {
      if (!controls?.lens) return;
      const lens = controls.lens;
      await setBridgeProperty(bridge.id, lens, name, value);
      // A lens chosen while the write was in flight keeps its own properties.
      setControls((c) =>
        c?.lens === lens
          ? { ...c, props: c.props.map((p) => (p.name === name ? { ...p, value } : p)) }
          : c,
      );
    });

  const prop = (name: string) => controls?.props.find((p) => p.name === name);
  const resolution = prop('resolution');
  const zoom = prop('zoom');
  const lensInfo = controls?.cameras.find((c) => c.id === controls.lens);
  const subtitle = lensSubtitle({ zoom: lensInfo?.zoom, ...parseSize(lensInfo?.maxResolution) });
  const flags = ['torch', 'lock'].map(prop).filter((p) => p?.type === 'boolean');

  return (
    <li aria-label={bridge.name} className="space-y-2 rounded-xl border border-divider p-3">
      <ShowBox
        label={bridge.name}
        checked={bridge.active}
        onChange={(active) =>
          update((c) => ({
            ...c,
            bridges: {
              ...c.bridges,
              [bridge.id]: { ...(c.bridges[bridge.id] ?? savedOf(bridge)), active },
            },
          }))
        }
      />
      <div className="flex flex-wrap items-center gap-2 text-sm text-content-secondary">
        <span className="rounded-full bg-surface-secondary px-2 py-0.5">{statusOf(bridge)}</span>
        <span>
          {bridge.host}:{bridge.port}
        </span>
      </div>
      {controls && (
        <>
          <select
            aria-label="Lens"
            className={selectClass}
            value={controls.lens ?? ''}
            onChange={(e) => void chooseLens(e.target.value)}
          >
            {controls.lens === null && (
              <option value="" disabled>
                Choose a lens…
              </option>
            )}
            {controls.cameras.map((c) => (
              <option key={c.id} value={c.id} disabled={!!c.unusable}>
                {c.label}
              </option>
            ))}
          </select>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {resolution?.options && (
            <select
              aria-label="Resolution"
              className={selectClass}
              value={String(resolution.value)}
              onChange={(e) => {
                const next = resolution.options?.find((o) => String(o) === e.target.value);
                if (next !== undefined) void set('resolution', next);
              }}
            >
              {resolution.options.map((o) => (
                <option key={String(o)} value={String(o)}>
                  {String(o)}
                </option>
              ))}
            </select>
          )}
          {zoom?.type === 'number' &&
            zoom.min !== undefined &&
            zoom.max !== undefined &&
            typeof zoom.value === 'number' &&
            Number.isFinite(zoom.value) && (
              <ZoomSlider
                key={controls.lens}
                min={zoom.min}
                max={zoom.max}
                step={zoom.step || 0.1}
                value={zoom.value}
                onCommit={(v) => set('zoom', v)}
              />
            )}
          <div className="flex gap-4">
            {flags.map((p) => (
              <label key={p?.name} className="flex min-h-11 items-center gap-2 text-base">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={p?.value === true}
                  onChange={(e) => p && void set(p.name, e.target.checked)}
                />
                {p?.name === 'torch' ? 'Torch' : 'Lock'}
              </label>
            ))}
          </div>
        </>
      )}
      {error && <Alert>{error}</Alert>}
      <Button
        variant="secondary"
        onClick={() => {
          update((c) => setHidden(c, bridge, true));
          if (bridge.manual)
            void shellApi()
              ?.bridges?.forgetBridge({ id: bridge.id })
              .catch(() => {});
        }}
      >
        Hide
      </Button>
    </li>
  );
}

function AddByAddress({ update }: { update: Update }) {
  const [host, setHost] = useState('');
  const [port, setPort] = useState(DEFAULT_PORT);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const portNumber = Number(port);
  const ready =
    host.trim() !== '' && Number.isInteger(portNumber) && portNumber > 0 && portNumber < 65536;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const address = { host: host.trim(), port: portNumber };
    setBusy(true);
    setError('');
    try {
      const added = await shellApi()?.bridges?.addBridge(address);
      if (!added) throw new Error('bridge settings need a newer app');
      update((c) => ({
        ...c,
        bridges: {
          ...c.bridges,
          [added.id]: { active: true, name: added.name, ...address },
        },
        manual: [...c.manual.filter((m) => m.id !== added.id), { id: added.id, ...address }],
      }));
      setHost('');
      setPort(DEFAULT_PORT);
    } catch (err) {
      setError(
        /newer (bridge|app)/.test(message(err))
          ? message(err)
          : `No KinoQ bridge at ${address.host}:${address.port}`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void add(e)} className="space-y-2">
      <span className="text-sm font-medium text-content-secondary">Add bridge by address</span>
      <div className="flex gap-2">
        <TextInput
          aria-label="Bridge host"
          placeholder="192.168.1.20"
          value={host}
          onChange={(e) => setHost(e.target.value)}
        />
        <div className="w-24 shrink-0">
          <TextInput
            aria-label="Bridge port"
            inputMode="numeric"
            value={port}
            onChange={(e) => setPort(e.target.value)}
          />
        </div>
      </div>
      <Button type="submit" disabled={!ready || busy}>
        Add
      </Button>
      {error && <Alert>{error}</Alert>}
    </form>
  );
}
